import React, {useState} from 'react';
import {Col, Dropdown, Row} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import Button from '@/components/ui/Button';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import axios from 'axios';
import {useStoreActions} from 'easy-peasy';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import TableComponent from '@/components/table/Table';
import EmailFetchTasksSettingsModal from './EmailFetchTasksSettingsModal';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import usePaginatedData from '@/utils/usePaginatedData';
import Form from 'react-bootstrap/Form';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import {ModalAlert} from '@/components/alerts';
import Icon from '@/components/icons/Icon';
import {hasPermission} from '@/utils/permissions/permissionDebug';

interface EmailFetchTask {
    id: number;
    account: {
        email: string;
    };
    prettyFolderPath: string;
    channelName: string;
    updatedOn: string;
    lastUpdated: string;
    createdOn: string;
    active: boolean;
}

interface UserData {
    permissions?: string[];
}

interface PaginatedDataResult<T> {
    data: T[];
    loading: boolean;
    currentPage: number | null;
    setCurrentPage: (page: number) => void;
    pages: number[] | null;
}

const EmailFetchTasksListView: React.FC = React.memo(() => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const setSearchQueryState = async (
        setSearchQuery: (query: string) => void,
        value: string,
        setPage: (page: number) => void
    ): Promise<void> => {
        setPage(1);
        setSearchQuery(value);
    };

    const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const {userData, loading: loadingUserData} = useOptimizedConfig();

    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [selectedEmailFetchTaskId, setEmailFetchTaskId] = useState<number>(0);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [taskToDelete, setTaskToDelete] = useState<{ id: number, account: any } | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const userCanCreateEmailFetchTasks = hasPermission('fs.email_sync.fetch_task.create', userData?.permissions, true);
    const userCanChangeEmailFetchTasks = hasPermission('fs.email_sync.fetch_task.update', userData?.permissions, true);
    const userCanDeleteEmailFetchTasks = hasPermission('fs.email_sync.fetch_task.delete', userData?.permissions, true);
    const userCanAccessEmailFetchTasksResource = hasPermission('fs.email_sync.fetch_task.read', userData?.permissions, true);


    const {
        data: emailFetchTasks,
        loading,
        currentPage,
        setCurrentPage,
        pages,
    } = usePaginatedData(window.URLS.emailFetchTasksResource, {
        storeResolver: 'emailFetchTasks',
        requestParams: {
            params: {
                query: searchQuery,
            },
        },
    });

    const inputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value, setCurrentPage);
    };

    const active = currentPage;
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };



    const deleteAction = useStoreActions((actions: any) => actions.emailFetchTasks.deleteOne);

    const openSettingsModal = (emailFetchTaskId: number): void => {
        setEmailFetchTaskId(emailFetchTaskId);
        setSettingsModalOpen(true);
    };

    const closeSettingsModal = (): void => {
        setSettingsModalOpen(false);
        setEmailFetchTaskId(0);
    };

    const openDeleteConfirm = (id: number, account: any): void => {
        setTaskToDelete({id, account});
        setDeleteConfirmOpen(true);
    };

    const closeDeleteConfirm = (): void => {
        setTaskToDelete(null);
        setDeleteConfirmOpen(false);
        setIsDeleting(false);
    };

    const confirmDelete = async (): Promise<void> => {
        if (!taskToDelete) {return;}

        setIsDeleting(true);
        try {
            const url = `${window.URLS.emailFetchTasksResource}/${taskToDelete.id}`;
            await axios.delete(url);
            deleteAction(taskToDelete.id);
            closeDeleteConfirm();
        } catch (error) {
            setIsDeleting(false);
            throw error;
        }
    };

    const deleteOne = (id: number, account: any): void => {
        openDeleteConfirm(id, account);
    };

    const columns = [
        {
            accessorKey: 'account.email',
            header: i18n.t('th_account.text'),
        },
        {
            accessorKey: 'prettyFolderPath',
            header: i18n.t('th_folder_path.text'),
        },
        {
            accessorKey: 'channelName',
            header: i18n.t('th_channel_name.text'),
        },
        {
            accessorKey: 'updatedOn',
            header: i18n.t('th_last_modified.text'),
            accessorFn: (cell: any) => {
                if (!cell.lastUpdated || cell.lastUpdated === '-') {
                    return configLoaded ? formatDateTime(cell.createdOn, true, {setZone: false},
                    ) : '';
                }

                return configLoaded ? formatDateTime(cell.lastUpdated, true, {setZone: false},
                ) : '';
            },
        },
        {
            accessorKey: 'active',
            header: i18n.t('th_active.text'),
            cell: (cell: any) => {
                if (cell.getValue() === true) {
                    return (
                        <Icon name="mdi-check" className="text-success" />
                    );
                } else {
                    return (
                        <Icon name="mdi-close" className="text-danger" />
                    );
                }
            },
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: any) => {
                return (
                    <>
                        <div className="d-inline-block">
                            <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                                <Dropdown className="d-inline-block">
                                    <Dropdown.Toggle
                                        as={Button}
                                        variant="outline-info"
                                        size="sm"
                                        icon="mdi-dots-horizontal"
                                        className="dropdown-toggle-no-caret"
                                        data-cy="email-fetch-tasks-settings-toggle"
                                        id="settings-dropdown-1"
                                    />

                                    <Dropdown.Menu align="end">
                                        {
                                            userCanChangeEmailFetchTasks ? (
                                                <Dropdown.Item
                                                    onClick={() => openSettingsModal(cell.row.original.id)}
                                                    data-cy="email-fetch-tasks-settings-open-button"
                                                >
                                                    <Icon name="mdi-settings" className="mr-2" />  {i18n.t('jt_settings.text')}
                                                </Dropdown.Item>
                                            ) : (
                                                <Tooltip
                                                    content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                                    placement="top"
                                                >
                                                    <div
                                                        className="ml-auto"
                                                        style={{
                                                            cursor: 'not-allowed',
                                                            display: 'flex',
                                                        }}
                                                    >
                                                        <Dropdown.Item
                                                            style={{pointerEvents: 'none'}}
                                                            disabled
                                                            data-cy="email-fetch-tasks-list-btn-1">
                                                            <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                                        </Dropdown.Item>
                                                    </div>
                                                </Tooltip>
                                            )
                                        }
                                        {
                                            userCanDeleteEmailFetchTasks ? (
                                                <Dropdown.Item
                                                    onClick={() => deleteOne(cell.row.original.id, cell.row.original.account)}
                                                >
                                                    <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                                </Dropdown.Item>
                                            ) : (
                                                <Tooltip
                                                    content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                                    placement="top"
                                                >
                                                    <div
                                                        className="ml-auto"
                                                        style={{
                                                            cursor: 'not-allowed',
                                                            display: 'flex',
                                                        }}
                                                    >
                                                        <Dropdown.Item
                                                            style={{pointerEvents: 'none'}}
                                                            disabled
                                                            data-cy="email-fetch-tasks-list-btn">
                                                            <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                                        </Dropdown.Item>
                                                    </div>
                                                </Tooltip>
                                            )
                                        }
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Tooltip>
                        </div>
                    </>
                );
            },
        },
    ];



    if (!userCanAccessEmailFetchTasksResource && !loadingUserData) {
        return (
            <>
                <HelpTextBlock helpText={i18n.t('ht_email_fetch.text')} />

                <div className="alert alert-danger" role="alert"> {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    return (
        <>
            <HelpTextBlock helpText={i18n.t('ht_email_fetch.text')} />
            <Row>
                <Col lg={3}>
                    <Form className="mb-2 d-flex align-items-center search-box" onSubmit={(e: React.FormEvent) => {
                        e.preventDefault();
                    }}>
                        <Form.Control
                            type="text"
                            placeholder={i18n.t('es_search.text')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => inputChange(event)}
                            data-cy="eval-search-field"
                        />
                    </Form>
                </Col>
                <Col lg={9} className="d-flex justify-content-end">
                    <div>
                        {
                            userCanCreateEmailFetchTasks ? (
                                <Button
                                    variant="info"
                                    size="sm"
                                    icon="mdi-plus"
                                    onClick={() => setSettingsModalOpen(true)}
                                >
                                    {i18n.t('jt_add_new_task.text')}
                                </Button>
                            ) : (
                                <Tooltip
                                    content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                    placement="top"
                                >
                                    <div style={{cursor: 'not-allowed', display: 'flex'}}>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            icon="mdi-plus"
                                            disabled
                                            style={{pointerEvents: 'none'}}
                                            data-cy="email-fetch-tasks-list-plus-btn">
                                            {i18n.t('jt_add_new_task.text')}
                                        </Button>
                                    </div>
                                </Tooltip>
                            )
                        }
                    </div>
                </Col>
            </Row>

            {
                loading ? (
                    <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px"><div /></LoadingWrapper>
                ) : (
                    <div className="card">
                        <div className="card-body">
                            <TableComponent data={emailFetchTasks} columns={columns} />
                        </div>
                        <PaginationComponent
                            pages={pages}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                        />

                        {
                            settingsModalOpen ? (
                                <EmailFetchTasksSettingsModal
                                    modalOpen={settingsModalOpen}
                                    closeModal={closeSettingsModal}
                                    activeEmailFetchTask={selectedEmailFetchTaskId}
                                />
                            ) : null
                        }
                    </div>
                )
            }
            <ModalAlert
                isOpen={deleteConfirmOpen}
                onClose={closeDeleteConfirm}
                variant="error"
                title={i18n.t('jt_confirm_deletion.text')}
                message={taskToDelete ? i18n.t('jt_tasks_are_you.text', {account: taskToDelete.account}) : ''}
                actions={[
                    {
                        label: i18n.t('jt_delete.text'),
                        onClick: confirmDelete,
                        variant: 'danger',
                        loading: isDeleting
                    }
                ]}
                dismissible={!isDeleting}
            />
        </>
    )
});

export default EmailFetchTasksListView; 